import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { resetIdleTimeout } from '../../services/idleTimeoutService';

/**
 * Dialog component that warns the user about impending logout due to inactivity
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when the dialog is closed
 * @param {number} props.countdownDuration - Duration of the countdown in milliseconds
 * @returns {JSX.Element} IdleTimeoutDialog component
 */
const IdleTimeoutDialog = ({ open, onClose, countdownDuration = 60000 }) => {
  const [countdown, setCountdown] = useState(countdownDuration);
  const [intervalId, setIntervalId] = useState(null);
  
  // Start countdown when dialog opens
  useEffect(() => {
    if (open) {
      const id = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1000) {
            clearInterval(id);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
      
      setIntervalId(id);
      setCountdown(countdownDuration);
    } else {
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [open, countdownDuration]);
  
  // Format seconds for display
  const formatSeconds = (ms) => {
    return Math.ceil(ms / 1000);
  };
  
  // Calculate progress percentage for the circular progress
  const calculateProgress = () => {
    return (countdown / countdownDuration) * 100;
  };
  
  // Handle continue session button click
  const handleContinue = () => {
    resetIdleTimeout();
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleContinue}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Session Timeout Warning
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" my={2}>
          <Box position="relative" display="inline-flex" mb={2}>
            <CircularProgress
              variant="determinate"
              value={calculateProgress()}
              size={80}
              thickness={4}
              color="error"
            />
            <Box
              top={0}
              left={0}
              bottom={0}
              right={0}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="h6" component="div" color="text.secondary">
                {formatSeconds(countdown)}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body1" align="center">
            Your session is about to expire due to inactivity.
          </Typography>
          <Typography variant="body1" align="center" mt={1}>
            You will be automatically logged out in {formatSeconds(countdown)} seconds.
          </Typography>
          <Typography variant="body2" align="center" mt={2} color="text.secondary">
            Click "Continue Session" to remain logged in.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleContinue} 
          color="primary" 
          variant="contained"
          autoFocus
        >
          Continue Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutDialog;
